import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  Controller,
  FormProvider,
  Resolver,
  useForm,
  useWatch,
} from 'react-hook-form';

import { addMinorOtherInfoRequest } from '@/api/operation';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { LoadingButton } from '@/components/Form/LoadingButton';
import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';

import { getPhoneSlotProps } from '@/constants/countries';
import {
  DEFAULT_MINOR_OTHER_FORM_VALUES,
  getMinorOtherValidationSchema,
  MinorOtherFormType,
} from '@/constants/minor';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';
import { selectTerritories } from '@/redux/countries/selectors';
import { selectOperationUser } from '@/redux/operation/selectors';

import { getAPIFormError } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { getPhoneCode } from '@/utils/phoneNumber';

import { MinorQuestionForm } from './MinorQuestionForm';
import { QuestionProps } from './QuestionDrawer.interface';

import {
  FullWidthRadioGroup,
  QuestionTitle,
  RadioButtonContainer,
  StyledRadio,
} from './QuestionDrawer.styled';

export function MinorQuestionOther({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const minorUser = useAppSelector(selectOperationUser);
  const { phoneCodes } = useAppSelector(selectTerritories);
  const { jointAccountRelationshipOptions } =
    useAppSelector(selectConfigsState);
  const relationshipOptions = jointAccountRelationshipOptions.map(option => ({
    ...option,
    text: t(String(option.i18nKey)),
  }));

  const methods = useForm<MinorOtherFormType>({
    resolver: yupResolver(
      getMinorOtherValidationSchema(),
    ) as Resolver<MinorOtherFormType>,
    mode: 'onChange',
    defaultValues: {
      ...DEFAULT_MINOR_OTHER_FORM_VALUES,
      phoneCode: getPhoneCode(minorUser?.tenant || ''),
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    resetField,
  } = methods;

  const hasPhone = useWatch({ control, name: 'hasPhone' });

  useEffect(() => {
    if (hasPhone === 'no') {
      resetField('email', { defaultValue: '' });
      resetField('phoneNumber', { defaultValue: '' });
    }
  }, [hasPhone, resetField]);

  const { mutate, isPending, error } = useMutation({
    mutationFn: addMinorOtherInfoRequest,
    onSuccess: onAnswered,
  });

  const apiFormError = getAPIFormError<{ phone: string; email: string }>(error);

  const onSubmit = (data: MinorOtherFormType) => {
    const trimmedPhone = data.phoneNumber?.trim();
    const hasPhoneContact = data.hasPhone === 'yes';
    mutate({
      clientId: minorUser?.id || '',
      operationId: minorUser?.operationId || '',
      payload: {
        relationship: data.relationship,
        ...(hasPhoneContact
          ? {
              email: data.email,
              ...(trimmedPhone
                ? { phone: `${data.phoneCode}${trimmedPhone}` }
                : {}),
            }
          : {}),
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <MinorQuestionForm
        {...props}
        isLoading={isPending}
        answerError={!apiFormError.phone && !apiFormError.email ? error : null}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack direction="column" gap={4} alignItems="stretch">
          <Stack direction="column" gap={1} alignItems="stretch">
            <Typography variant="body1">
              {t('minorOthers.form.relationship.label')}
            </Typography>
            <Select
              {...getDataTestId('minor-relationship-select')}
              options={relationshipOptions}
              control={control}
              name="relationship"
              displayEmpty
              error={!!errors.relationship}
              helperText={errors.relationship?.message}
              rules={{
                required: t('minorOthers.form.relationship.required'),
              }}
              fullWidth
              disabled={isPending}
              placeholder={t('minorOthers.form.relationship.placeholder')}
              useDrawerOnMobile
              withSearchBar
            />
          </Stack>

          <Stack direction="column" gap={1} alignItems="stretch">
            <QuestionTitle variant="body1">
              {t('minorOthers.form.hasPhone.label')}
            </QuestionTitle>
            <Controller
              name="hasPhone"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioButtonContainer>
                  <FullWidthRadioGroup value={value || ''} onChange={onChange}>
                    <FormControlLabel
                      value="yes"
                      control={<StyledRadio />}
                      label={t('minorOthers.form.hasPhone.yes')}
                      {...getDataTestId('minor-has-phone-yes')}
                    />
                    <FormControlLabel
                      value="no"
                      control={<StyledRadio />}
                      label={t('minorOthers.form.hasPhone.no')}
                      {...getDataTestId('minor-has-phone-no')}
                    />
                  </FullWidthRadioGroup>
                </RadioButtonContainer>
              )}
            />
          </Stack>

          {hasPhone === 'yes' && (
            <>
              <Stack direction="column" gap={1} alignItems="stretch">
                <Typography variant="body1">
                  {t('minorOthers.form.phone.label')}
                </Typography>
                <Grid container columnSpacing={1.5} rowSpacing={2}>
                  <Grid item xs={5}>
                    <Select
                      options={phoneCodes}
                      control={control}
                      name="phoneCode"
                      fullWidth
                      disabled={isPending}
                    />
                  </Grid>
                  <Grid item xs={7}>
                    <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field: { value } }) => (
                        <TextInput
                          type="tel"
                          inputMode="tel"
                          name="phoneNumber"
                          control={control}
                          placeholder={t('minorOthers.form.phone.placeholder')}
                          error={!!errors.phoneNumber || !!apiFormError.phone}
                          helperText={
                            errors.phoneNumber?.message || apiFormError.phone
                          }
                          inputProps={getPhoneSlotProps(value || '')}
                          disabled={isPending}
                          {...getDataTestId('minor-phone-input')}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>

              <Stack direction="column" gap={1} alignItems="stretch">
                <Typography variant="body1">
                  {t('minorOthers.form.email.label')}
                </Typography>
                <TextInput
                  name="email"
                  control={control}
                  error={!!errors.email || !!apiFormError.email}
                  helperText={errors.email?.message || apiFormError.email}
                  disabled={isPending}
                  fullWidth
                  placeholder={t('minorOthers.form.email.placeholder')}
                  type="email"
                  {...getDataTestId('minor-email-input')}
                />
              </Stack>
            </>
          )}

          <LoadingButton
            {...getDataTestId('minor-others-submit-button')}
            type="submit"
            variant="contained"
            fullWidth
            isLoading={isPending}
            disabled={!isValid || isPending}
          >
            {t('minorOthers.form.button.proceed')}
          </LoadingButton>
        </Stack>
      </MinorQuestionForm>
      <FormErrorLogger formName="MinorQuestionOther" control={control} />
    </FormProvider>
  );
}
